import { useEffect, useRef } from 'react';
import Flipbook from './Flipbook';

const IMG = {
  hero: '/assets/strata-hero.png',
  detail: '/assets/strata-detail.png',
  wide: '/assets/strata-wide.png',
};

function NaturalParallax({ src, alt, testid, intensity = 50, scale = 1.08, wrapperClassName = '' }) {
  const ref = useRef(null);
  useEffect(() => {
    let raf;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const el = ref.current;
        if (!el || !el.parentElement) return;
        const rect = el.parentElement.getBoundingClientRect();
        const vh = window.innerHeight;
        const span = vh / 2 + rect.height / 2;
        if (span <= 0) return;
        const p = (rect.top + rect.height / 2 - vh / 2) / span;
        const clamped = Math.max(-1, Math.min(1, p));
        el.style.transform = `translate3d(0, ${-clamped * intensity}px, 0) scale(${scale})`;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(raf);
    };
  }, [intensity, scale]);
  return (
    <div className={`relative w-full overflow-hidden ${wrapperClassName}`}>
      <img
        ref={ref}
        src={src}
        alt={alt}
        data-testid={testid}
        className="block w-full h-auto will-change-transform"
        loading="lazy"
      />
    </div>
  );
}

export default function Strata() {
  return (
    <section id="strata" data-testid="strata-section" className="relative bg-bg">
      <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-24 md:py-40">
        {/* Header */}
        <div className="grid grid-cols-12 gap-6 md:gap-10 items-end">
          <div className="col-span-12 md:col-span-7 reveal">
            <span className="kicker">Object Study · 2026</span>
            <h2 className="font-display font-light text-ink mt-3 leading-[0.95] tracking-tight text-[clamp(2.8rem,7vw,6rem)]">
              Strata <span className="italic text-crimson">Coffee</span> Table.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4 md:col-start-9 reveal text-sub text-base md:text-lg leading-relaxed">
            A sculptural furniture piece formed from layered earth — built through a slow, layered
            process that allows its form to develop naturally during making.
          </div>
        </div>

        {/* Main image + descriptive copy */}
        <div className="mt-14 md:mt-20 grid grid-cols-12 gap-6 md:gap-10 items-start">
          <div className="col-span-12 md:col-span-7 reveal">
            <NaturalParallax
              src={IMG.hero}
              alt="Strata coffee table — layered earth form, side view"
              testid="strata-hero"
              intensity={40}
              scale={1.08}
              wrapperClassName="rounded-sm bg-surface"
            />
          </div>
          <div className="col-span-12 md:col-span-5 md:pl-4 md:pt-10 reveal">
            <span className="kicker">About Strata</span>
            <div className="mt-5 space-y-5 text-base md:text-lg leading-relaxed text-sub">
              <p>
                Strata Coffee Table is a sculptural furniture piece formed from layered earth. The
                table is built through a slow, layered process that allows its form to develop
                naturally during making.
              </p>
              <p>
                The table is conceived as a grounded object within the domestic space. Low, quiet,
                and deliberate, it invites touch and presence rather than attention. Its form is
                defined by weight and proportion, allowing the material itself to remain visible and
                honest.
              </p>
              <p>
                Strata Coffee Table sits between furniture and object. It does not imitate natural
                forms, but instead adopts the logic of the earth — layered, compressed, and resolved
                through making.
              </p>
            </div>

            <ul className="mt-10 space-y-3 text-sm text-ink/85">
              <li className="flex items-start gap-3"><span className="mt-2 h-px w-6 bg-ink/40" /> Layered &amp; compressed earth</li>
              <li className="flex items-start gap-3"><span className="mt-2 h-px w-6 bg-ink/40" /> Honest, untreated surface</li>
              <li className="flex items-start gap-3"><span className="mt-2 h-px w-6 bg-ink/40" /> Between furniture &amp; object</li>
            </ul>
          </div>
        </div>

        {/* Secondary detail image + wide image */}
        <div className="mt-10 md:mt-16 grid grid-cols-12 gap-6">
          <figure className="col-span-12 md:col-span-5 reveal">
            <NaturalParallax
              src={IMG.detail}
              alt="Strata coffee table — top view detail"
              testid="strata-detail"
              intensity={30}
              scale={1.08}
              wrapperClassName="rounded-sm bg-surface"
            />
            <figcaption className="mt-4 flex items-center justify-between text-xs tracking-[0.28em] uppercase text-sub">
              <span>Detail · Top view</span>
              <span>2026</span>
            </figcaption>
          </figure>
          <figure className="col-span-12 md:col-span-7 reveal">
            <NaturalParallax
              src={IMG.wide}
              alt="Strata coffee table in situ"
              testid="strata-wide"
              intensity={40}
              scale={1.1}
              wrapperClassName="rounded-sm bg-surface"
            />
            <figcaption className="mt-4 flex items-center justify-between text-xs tracking-[0.28em] uppercase text-sub">
              <span>In situ</span>
              <span>Bristol Studio</span>
            </figcaption>
          </figure>
        </div>

        {/* AutoCAD plan */}
        <figure className="mt-10 md:mt-16 reveal">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-5">
            <div>
              <span className="kicker">Technical Drawing</span>
              <h3 className="font-display text-3xl md:text-4xl tracking-tight mt-2 leading-tight">
                AutoCAD <span className="italic">Detailed Plan</span>.
              </h3>
            </div>
            <span className="text-[11px] tracking-[0.28em] uppercase text-sub">Sheet 01 · Strata</span>
          </div>
          <NaturalParallax
            src="/assets/strata-plan.jpg"
            alt="Strata coffee table — AutoCAD detailed plan"
            testid="strata-plan"
            intensity={50}
            scale={1.06}
            wrapperClassName="rounded-sm bg-surface border border-line"
          />
        </figure>

        {/* Research & Development book */}
        <div className="mt-16 md:mt-24">
          <div className="reveal max-w-3xl mb-8 md:mb-12">
            <span className="kicker">Process</span>
            <h3 className="font-display text-3xl md:text-4xl tracking-tight mt-2 leading-tight">
              Research &amp; <span className="italic">Development</span> Book.
            </h3>
          </div>
          <div className="reveal">
            <Flipbook
              pdfUrl="/assets/strata-book.pdf"
              kicker="Process Book"
              title="Strata · R&D"
              subtitle="Material studies · iterations · final piece."
            />
          </div>
        </div>
      </div>
    </section>
  );
}
